import React from 'react';
import type { TFunction } from 'i18next';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import {
  GetCompanyDto,
  GetPositionExtendedDto,
  GetProfileDto,
  GetTeamDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { nameof } from '../../../utils/objectUtils';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { PositionTypeObj } from '../../../common/pokCore/validation/enums';
import { teamName } from '../../../utils/teamName';

import { PositionInfo } from './PositionInfo';

interface PositionSearchProps {
  hasClient?: boolean;
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  refresh: boolean;
}

const getRelationNames = (
  items: GetCompanyDto[] | GetProfileDto[] | undefined,
  fieldName: keyof GetCompanyDto | keyof GetProfileDto,
  searchFieldName: string,
  orderBy: string,
) => {
  let names = items?.map(item => {
    if ('shortname' in item && fieldName === 'shortname') {
      return item.shortname;
    }
    return item.name;
  });
  if (names && orderBy !== searchFieldName) {
    names = names.sort((a, b) => a.localeCompare(b, 'pl'));
  }
  return names?.join(', ');
};

const getCompanySummary = (
  items: GetCompanyDto[] | undefined,
  orderBy: string,
) => {
  let list = items?.map(item => ({
    name: item.shortname,
    systemNumber: item.systemNumber,
  }));
  if (list && orderBy !== 'companies.shortname') {
    list = [...list].sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  }
  return {
    names: list?.map(item => item.name).join(', '),
    systemNumbers: list?.map(item => item.systemNumber).join(', '),
  };
};

const getTeamSummary = (items: GetTeamDto[] | undefined, orderBy: string) => {
  let list = items?.map(team => ({
    name: teamName(team, team?.company?.shortname),
    systemNumber: team?.systemNumber,
  }));
  if (list && orderBy !== 'teams.name') {
    list = [...list].sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  }
  return {
    names: list?.map(item => item.name).join(', '),
    systemNumbers: list?.map(item => item.systemNumber).join(', '),
  };
};

export const positionForGrid = (
  t: TFunction<'clientLng', 'clientLng'>,
  lang: string,
  position: GetPositionExtendedDto,
  orderBy: string = '',
) => {
  const companySummary = getCompanySummary(position.companies, orderBy);
  const teamSummary = getTeamSummary(position.teams, orderBy);
  const profileNames = getRelationNames(
    position.profiles,
    nameof<GetProfileDto>('name'),
    'profiles.name',
    orderBy,
  );
  return {
    key: position.id,
    values: {
      'position.systemNumber': position.systemNumber,
      'position.name': position.name,
      'position.groupName': position.groupName,
      'dictionary.value':
        position.dictionary?.shortname ||
        (lang === 'PL'
          ? position.dictionary?.value
          : position.dictionary?.valueEn),
      'dictionary.systemNumber': position.dictionary?.systemNumber,
      'position.positionType': t(PositionTypeObj[position.positionType]),
      'client.name': position.client.name,
      'client.systemNumber': position.client.systemNumber,
      'specialistTeams.name':
        position.positionSpecialistTeams
          ?.map(p => teamName(p.team, p.team?.company?.shortname))
          .join(', ') ?? '',
      'specialistTeams.systemNumber':
        position.positionSpecialistTeams
          ?.map(p => p.team?.systemNumber)
          .join(', ') ?? '',
      'teams.name': teamSummary.names,
      'teams.systemNumber': teamSummary.systemNumbers,
      'companies.shortname': companySummary.names,
      'companies.systemNumber': companySummary.systemNumbers,
      'profiles.name': profileNames,
      status: <PositionInfo id={position.id} />,
    },
    isInactive: !position.active,
  };
};

export const PositionSearch: React.FC<PositionSearchProps> = props => {
  const { t, tk } = useTranslation('clientLng');

  const columns: GridColumn[] = [
    { header: t(tk.common.sysNumber), property: 'position.systemNumber' },
    { header: t(tk.common.name), property: 'position.name' },
    ...(props.hasClient
      ? []
      : [
          { header: t(tk.common.contractor), property: 'client.name' },
          {
            header: `${t(tk.common.contractor)} - ${t(tk.common.sysNumber)}`,
            property: 'client.systemNumber',
            exportOnly: true,
          },
        ]),
    { header: t(tk.position.groupName), property: 'position.groupName' },
    {
      header: t(tk.position.type),
      property: 'position.positionType',
      noFilter: true,
    },
    { header: t(tk.position.communicationCanal), property: 'dictionary.value' },
    {
      header: `${t(tk.position.communicationCanal)} - ${t(tk.common.sysNumber)}`,
      property: 'dictionary.systemNumber',
      exportOnly: true,
    },
    { header: t(tk.position.profiles), property: 'profiles.name' },
    { header: t(tk.common.companies), property: 'companies.shortname' },
    {
      header: `${t(tk.common.companies)} - ${t(tk.common.sysNumber)}`,
      property: 'companies.systemNumber',
      exportOnly: true,
    },
    { header: t(tk.common.teams), property: 'teams.name' },
    {
      header: `${t(tk.common.teams)} - ${t(tk.common.sysNumber)}`,
      property: 'teams.systemNumber',
      exportOnly: true,
    },
    { header: t(tk.position.specialTeams), property: 'specialistTeams.name' },
    {
      header: `${t(tk.position.specialTeams)} - ${t(tk.common.sysNumber)}`,
      property: 'specialistTeams.systemNumber',
      exportOnly: true,
    },
    { header: '', property: 'status', noSorting: true, noFilter: true },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      refresh={props.refresh}
      initialOrderColumn="position.name"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={
        props.hasClient
          ? t(tk.position.noPositionsForClient)
          : t(tk.position.noPositions)
      }
      onRowClick={props.onRowClick}
      withHistory={true}
      withFilterInColumns={true}
      withOverflowXAuto
      showExport={true}
    />
  );
};
